import React, { useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import userHasPrivs from 'Helper/Auth/userHasPrivs'
import ConfigMenu from './ConfigMenu.jsx'
import Billing from './Billing/Billing.jsx'
import { Routes, Route, Outlet } from 'react-router-dom'
import { loadLobbyLoginConfig } from 'actions/appConfigActions'
import WindowContainer from 'GeneralUI/WindowContainer/WindowContainer.jsx'
import Async from '../Functional/Async.jsx'
import AttachmentTags from './General/AttachmentTags.jsx'
// async loads to support code splitting
const SystemOverview = Async(
  React.lazy(() => import('./AppConfig/SystemOverview.jsx'))
)
const SupportFunctions = Async(
  React.lazy(() => import('./AppConfig/SupportFunctions.jsx'))
)
const OAuthClients = Async(
  React.lazy(() => import('./AppConfig/OAuthClients.jsx'))
)
const FeatureFlags = Async(
  React.lazy(() => import('./AppConfig/FeatureFlags.jsx'))
)
const SystemAdminScreens = Async(
  React.lazy(() => import('./AppConfig/SystemAdminScreens.jsx'))
)
const ModalConfig = Async(
  React.lazy(() => import('./AppConfig/ModalConfig.jsx'))
)
const InstallConfigs = Async(
  React.lazy(() => import('./AppConfig/InstallConfigs.jsx'))
)
const CustomerExport = Async(
  React.lazy(() => import('./AppConfig/CustomerExport.jsx'))
)
const UserPrivileges = Async(
  React.lazy(() => import('./UserPrivileges/UserPrivileges.jsx'))
)
const SystemAdminSearch = Async(
  React.lazy(() => import('./AppConfig/SystemAdminSearch.jsx'))
)
const SystemAdminPharmacy = Async(
  React.lazy(() => import('../Config/Pharmacy/PharmacyManagementWindow.jsx'))
)
const SystemAdminStateMachine = Async(
  React.lazy(() =>
    import('../Config/StateMachine/StateMachineManagementWindow.jsx')
  )
)
const SystemAdminWindows = Async(
  React.lazy(() => import('./AppConfig/SystemAdminWindows.jsx'))
)
export const LobbyLoginSettings = Async(
  React.lazy(() => import('./LobbyLogin/LobbyLoginSettings.jsx'))
)
const RoomingLocalSettings = Async(
  React.lazy(() => import('./General/GeneralLocalSettings.jsx'))
)
const PrinterTemplates = Async(
  React.lazy(() => import('./General/PrinterTemplates.jsx'))
)
const TextSummary = Async(
  React.lazy(() => import('./General/TextSummary.jsx'))
)
const ReportImages = Async(
  React.lazy(() => import('./General/ReportImages.jsx'))
)
const PrintDialogs = Async(
  React.lazy(() => import('./General/PrintDialogs.jsx'))
)
const InventoryLocations = Async(
  React.lazy(() => import('./General/InventoryLocations.jsx'))
)
const PurchaseOrderStatuses = Async(
  React.lazy(() => import('./General/PurchaseOrderStatuses.jsx'))
)
const TreatmentSetStatuses = Async(
  React.lazy(() => import('./General/TreatmentSetStatuses.jsx'))
)
const SkintestStatuses = Async(
  React.lazy(() => import('./General/SkintestStatuses.jsx'))
)
const AccountStatuses = Async(
  React.lazy(() => import('./General/AccountStatuses.jsx'))
)
const VialSizes = Async(
  React.lazy(() => import('./General/VialSizes.jsx'))
)
const GlobalSettings = Async(
  React.lazy(() => import('./General/GlobalSettings.jsx'))
)
const DosingPlanContainer = Async(
  React.lazy(() => import('./DosingPlan/DosingPlanContainer.jsx'))
)
const SystemAdminWorkflows = Async(
  React.lazy(() => import('./AppConfig/SystemAdminWorkflows.jsx'))
)
const LobbyDashboardSettings = Async(
  React.lazy(() => import('./LobbyDashboard/LobbyDashboardSettings.jsx'))
)
const DataImporters = Async(
  React.lazy(() => import('./AppConfig/DataImporters/DataImporters.jsx'))
)
const SystemAdminQuickAccess = Async(
  React.lazy(() => import('./AppConfig/SystemAdminQuickAccess.jsx'))
)
const PatientDisplayConfig = Async(
  React.lazy(() =>
    import('./AppConfig/PatientDisplayConfig/PatientDisplayConfig.jsx')
  )
)

const NotEnoughPrivs = Async(React.lazy(() => import('./NoReadPriv.jsx')))

const AdminWorkflows = () => {
  return <SystemAdminWorkflows type="adminDisplay" />
}
const PatientWorkflows = () => {
  return <SystemAdminWorkflows type="patientDisplay" />
}
const AdminScreens = () => {
  return <SystemAdminScreens type="adminDisplay" />
}
const DashboardScreens = () => {
  return <SystemAdminScreens type="nurseDashboard" />
}
const PatientScreens = () => {
  return <SystemAdminScreens type="patientDisplay" />
}
const AdminWindows = () => {
  return <SystemAdminWindows type="adminDisplay" />
}
const DashboardWindows = () => {
  return <SystemAdminWindows type="nurseDashboard" />
}
const PatientWindows = () => {
  return <SystemAdminWindows type="patientDisplay" />
}

export const PrivChecker = ({ priv, children }) => {
  const privileges = useSelector(state => state.appConfig.privileges)
  const userId = useSelector(state => state.auth.user_id)
  const hasPrivilege = useMemo(() => userHasPrivs(priv), [priv, userId])
  let privilege = useMemo(() => (privileges || []).find(privilege => privilege.name === priv), [priv, privileges])
  privilege = !privileges ? null : privilege
  if (hasPrivilege) {
    return children
  } else {
    const displayName = privilege ? privilege.display_name : 'Unknown'
    const category = privilege ? privilege.category : 'Unknown Category'
    return (
      <NotEnoughPrivs
        category={category}
        priv={displayName}
      />
    )
  }
}

const Router = () => {
  return (
    <Routes>
      <Route
        path={'dosing_plans'}
        element={
          <PrivChecker priv={'dosing_plan_read'}>
            <DosingPlanContainer />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/data_importers'}
        element={<DataImporters />}
      />
      <Route
        path={'sysadmin/customer_export'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <CustomerExport />
          </PrivChecker>
        }
      />
      <Route
        path={'lobby/dashboard'}
        element={
          <PrivChecker priv={'lobby_dashboard_settings_read'}>
            <LobbyDashboardSettings />
          </PrivChecker>
        }
      />
      {/* This route is actually defined in Components/AppRouter.jsx (see note there)
      <Route
        path={'lobby/login'}
        element={
          <PrivChecker priv={'lobby_login_settings_read'}>
            <LobbyLoginSettings />
          </PrivChecker>
        }
      /> */}
      {/*TODO: Not removing right now but I dont think there is a way to call this route? - AMH 3/9/26 */}
      <Route
        path={'rooming/display'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <RoomingLocalSettings />
          </PrivChecker>
        }
      />
      <Route
        path={'general/printer_templates'}
        element={
          <PrivChecker priv={'printers_templates_read'}>
            <PrinterTemplates />
          </PrivChecker>
        }
      />
      <Route
        path={'general/text_summary'}
        element={
          <PrivChecker priv={'text_summary_settings_read'}>
            <TextSummary />
          </PrivChecker>
        }
      />
      <Route
        path={'general/report_images'}
        element={
          <PrivChecker priv={'report_images_edit'}>
            <ReportImages />
          </PrivChecker>
        }
      />
      <Route
        path={'general/print_dialogs'}
        element={
          <PrivChecker priv={'print_dialogs_read'}>
            <PrintDialogs />
          </PrivChecker>
        }
      />
      <Route
        path={'general/inventory_locations'}
        element={
          <PrivChecker priv={'inventory_locations_read'}>
            <InventoryLocations />
          </PrivChecker>
        }
      />
      <Route
        path={'general/purchase_order_statuses'}
        element={
          <PrivChecker priv={'all_status_settings_edit'}>
            <PurchaseOrderStatuses />
          </PrivChecker>
        }
      />
      <Route
        path={'general/treatment_set_statuses'}
        element={
          <PrivChecker priv={'all_status_settings_edit'}>
            <TreatmentSetStatuses />
          </PrivChecker>
        }
      />
      <Route
        path={'general/skintest_statuses'}
        element={
          <PrivChecker priv={'all_status_settings_edit'}>
            <SkintestStatuses />
          </PrivChecker>
        }
      />
      <Route
        path={'general/account_statuses'}
        element={
          <PrivChecker priv={'all_status_settings_edit'}>
            <AccountStatuses />
          </PrivChecker>
        }
      />
      <Route
        path={'general/attachment_tags'}
        element={
          <PrivChecker priv={'all_status_settings_edit'}>
            <AttachmentTags />
          </PrivChecker>
        }
      />
      <Route
        path={'general/vial_sizes'}
        element={
          <VialSizes />
        }
      />
      <Route
        path={'general/global_settings'}
        element={
          <PrivChecker priv={'global_settings_read'}>
            <GlobalSettings />
          </PrivChecker>
        }
      />
      <Route
        exact
        path={'sysadmin/*'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SystemOverview />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/windows/admin'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <AdminWindows />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/support_functions'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SupportFunctions />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/feature_flags'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <FeatureFlags />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/oauth/clients'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <OAuthClients />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/patient_display'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <PatientDisplayConfig />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/modal'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <ModalConfig />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/install_configs'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <InstallConfigs />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/windows/patient'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <PatientWindows />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/windows/dashboard'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <DashboardWindows />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/screens/admin'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <AdminScreens />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/screens/patient'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <PatientScreens />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/screens/dashboard'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <DashboardScreens />
          </PrivChecker>
        }
      />
      <Route
        path={'sysAdmin/searchActions'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SystemAdminSearch />
          </PrivChecker>
        }
      />
      <Route
        path={'sysAdmin/pharmacyActions'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SystemAdminPharmacy />
          </PrivChecker>
        }
      />
      <Route
        path={'sysAdmin/stateMachineActions'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SystemAdminStateMachine />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/workflows/admin'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <AdminWorkflows />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/workflows/patient'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <PatientWorkflows />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/quickAccess'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SystemAdminQuickAccess />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/system_overview'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <SystemOverview />
          </PrivChecker>
        }
      />
      <Route
        path={'sysadmin/user_privileges'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <UserPrivileges />
          </PrivChecker>
        }
      />
      <Route
        path={'billing'}
        element={
          <PrivChecker priv={'sys_admin'}>
            <Billing />
          </PrivChecker>
        }
      />
      <Route
        path={'reports'}
        component={() => {
          return <div>nothing yet</div>
        }}
      />
    </Routes>
  )
}

const ConfigPanel = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    // componentDidMount
    dispatch(loadLobbyLoginConfig())
  }, [])

  return (
    <div className="config">
      <ConfigMenu />
      <WindowContainer>
        <Router />
        <Outlet />
      </WindowContainer>
    </div>
  )
}

export default ConfigPanel
